import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ControllerEliminarDiagnosticoPaciente } from "../../../Service/Service.diagnostico";
import { guardarExamenImagenesService } from "../../../Service/Service.examen";
import { CITA_FINALIZADA_SENTINEL } from "../../../utils/citaFinalizadaAlert";
import { hcError, hcExito } from "../hcSwal";
import HCModal from "../HCModal";
import { ChipGroup, CheckInline, DateBadge, ExamenPicker, Field, OrderBadge, Section, Segmented } from "../HCPiezas";

/* ============================================================================
   Orden de examen de imagen — migrado a HCModal.

   Qué NO cambia (verificado contra el backend):
   · El `form` que se envía es idéntico campo por campo, y sale por el mismo
     `guardarExamenImagenesService` -> POST /examen_registro_cita_imagen_orden.
   · El flujo de catálogo se conserva: se elige UNA categoría (`tiposEstudio`)
     y los exámenes se filtran por `examen.tipo === categoria.tipo`, igual que
     antes. Laboratorio no se toca desde aquí.
   · `numero_orden` sigue siendo el prefijo `IMG-<idcita>-CECOLON`; el
     correlativo `-001` lo añade el servidor.

   Qué cambia, y por qué:
   · Selección MÚLTIPLE de exámenes. El backend ya iteraba `IdExamenTipo`
     creando una solicitud y un procedimiento_cita_paciente por examen; el
     límite de "un solo examen" era una validación del front. OJO: cada examen
     marcado es una prestación facturable.
   · `sala` deja de nacer con el literal "n/a" y pasa a placeholder. La columna
     ya era nullable; se retira la validación del front que la exigía.
   · `motivo` pasa a ser obligatorio (antes no se validaba en absoluto).
   · `numero_orden` ya no es editable: era un input con `disable` (prop que no
     existe en React, así que en la práctica se podía escribir encima).
   · Prioridad pasa de tres toggles independientes a un segmented excluyente,
     y las condiciones del paciente se piden UNA vez en lugar de repetirse.
     AMBOS siguen sin persistir: no hay columna para ellos en
     `examen_imagen_orden` y no se inventa ninguna (decisión del cliente).
   ========================================================================== */

const PRIORIDADES = [
  { value: "rutina", label: "Rutina" },
  { value: "control", label: "Control" },
  { value: "urgente", label: "Urgente", peligro: true },
];

const CONDICIONES_INICIALES = [
  { id: 1, value: "1", label: "Puede movilizarse", checked: false },
  { id: 2, value: "2", label: "Puede retirarse vendas", checked: false },
  { id: 3, value: "3", label: "Requiere médico presente", checked: false },
  { id: 4, value: "4", label: "Tomar radiografía en sala", checked: false },
];

const FORM_VACIO = {
  idcita: 0,
  IdCategoriaExamen: 0,
  idpaciente: 0,
  fecha: moment().format("YYYY-MM-DD"),
  numero_orden: "",
  sala: "",
  motivo: "",
  resumen_clinico: "",
  IdExamenTipo: [],
};

export default function ModalOrdenImagen({
  show,
  onHide,
  onSaveSuccess,
  cita,
  paciente,
  tiposEstudio = [],
  examenes = [],
  IdCategoriaExamen,
  ListaCitaPaciente,
}) {
  const [tab, setTab] = useState("orden");
  const [form, setForm] = useState(FORM_VACIO);
  const [categoria, setCategoria] = useState(null); // categoría elegida (una)
  const [prioridad, setPrioridad] = useState("rutina"); // no persiste (ver cabecera)
  const [condiciones, setCondiciones] = useState(CONDICIONES_INICIALES); // no persiste
  const [loading, setLoading] = useState(false);
  const [intentado, setIntentado] = useState(false);
  const motivoRef = useRef(null);
  const estudioRef = useRef(null);

  // Al abrir: mismos valores de arranque que `initModal` del modal viejo,
  // salvo `sala`, que ya no nace con "n/a".
  useEffect(() => {
    if (!show || !cita) return;
    setForm({
      ...FORM_VACIO,
      idcita: cita.idcita,
      idpaciente: cita.pacientes?.idpaciente,
      IdCategoriaExamen,
      numero_orden: `IMG-${cita.idcita}-CECOLON`,
      fecha: moment().format("YYYY-MM-DD"),
    });
    setCategoria(null);
    setPrioridad("rutina");
    setCondiciones(CONDICIONES_INICIALES.map((c) => ({ ...c, checked: false })));
    setIntentado(false);
    setTab("orden");
  }, [show, cita, IdCategoriaExamen]);

  // Exámenes de la categoría elegida — mismo filtro que el modal viejo.
  const examenesCategoria = useMemo(() => {
    if (!categoria) return [];
    return examenes.filter((e) => e.tipo === categoria.tipo);
  }, [examenes, categoria]);

  // ChipGroup en modo simple manda `null` al pulsar la que ya estaba activa.
  // Cambiar (o quitar) la categoría limpia la selección: un examen pertenece a
  // una sola categoría, y era el origen del filtro por `tipo`.
  const elegirCategoria = (idExamenTipo) => {
    const cat = idExamenTipo
      ? tiposEstudio.find((t) => String(t.IdExamenTipo) === String(idExamenTipo)) || null
      : null;
    setCategoria(cat);
    setForm((f) => ({ ...f, IdExamenTipo: [] }));
  };

  const alternarExamen = (id) => {
    setForm((f) => {
      const set = new Set(f.IdExamenTipo);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...f, IdExamenTipo: [...set] };
    });
  };

  // ---- validación: qué falta, en vivo ----------------------------------
  const faltantes = useMemo(() => {
    const f = [];
    if (!form.IdExamenTipo || form.IdExamenTipo.length === 0) f.push("Estudio");
    if (!form.motivo || !form.motivo.trim()) f.push("Motivo");
    if (!form.fecha) f.push("Fecha");
    return f;
  }, [form]);

  const enfermedadActual = cita?.enfermedad_actual_cita?.[0]?.enfermedad_actual || "";

  const guardar = async () => {
    setIntentado(true);
    if (faltantes.length > 0) {
      // Foco + scroll al primer inválido.
      const destino = faltantes[0] === "Estudio" ? estudioRef.current : motivoRef.current;
      destino?.scrollIntoView({ behavior: "smooth", block: "center" });
      destino?.focus?.({ preventScroll: true });
      return;
    }

    setLoading(true);
    const data = await guardarExamenImagenesService(form);

    if (data === CITA_FINALIZADA_SENTINEL) {
      setLoading(false);
      return; // el aviso específico ya se mostró
    }
    if (!data || data.success === false) {
      hcError("No se pudo guardar", "Intenta de nuevo en unos segundos.");
      setLoading(false);
      return;
    }

    hcExito("Orden de imagen enviada", `${form.IdExamenTipo.length} examen(es).`);
    setLoading(false);
    if (onSaveSuccess) await onSaveSuccess();
    cerrar();
  };

  const cerrar = () => {
    setForm(FORM_VACIO);
    setCategoria(null);
    onHide && onHide();
  };

  const borrarDiagnostico = async (id) => {
    const r = await ControllerEliminarDiagnosticoPaciente(id);
    if (r && r.success && ListaCitaPaciente) await ListaCitaPaciente();
  };

  const diagnosticos = cita?.diagnostico_cita_paciente || [];
  const hayDatos = Boolean(
    (form.IdExamenTipo && form.IdExamenTipo.length) || form.motivo || form.resumen_clinico || form.sala
  );

  const subtitulo = [
    paciente?.nombres_completos || cita?.pacientes?.nombres_completos,
    paciente?.edad ? `${paciente.edad} a` : null,
    cita?.idcita ? `Cita ${cita.idcita}` : null,
    [cita?.doctor?.prefijo, cita?.doctor?.nombre].filter(Boolean).join(" ") || null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <HCModal
      isOpen={!!show}
      size="lg"
      title="Orden de examen de imagen"
      subtitle={subtitulo}
      meta={[
        <DateBadge key="f" value={moment(form.fecha).format("DD/MM/YYYY")} label="Fecha de la orden" />,
        <OrderBadge key="o" code={form.numero_orden} />,
      ]}
      tabs={[
        { key: "orden", label: "Orden" },
        { key: "diagnostico", label: "Diagnóstico", count: diagnosticos.length },
      ]}
      activeTab={tab}
      onTabChange={setTab}
      validation={{ missing: faltantes }}
      primaryLabel="Agregar orden"
      onPrimary={guardar}
      primaryLoading={loading}
      onClose={cerrar}
      onCancel={cerrar}
      hayCambiosSinGuardar={hayDatos}
    >
      {tab === "orden" ? (
        <>
          <Section title="1 · Estudio solicitado" hint="Elige una categoría, luego el examen">
            <div ref={estudioRef} tabIndex={-1}>
              <ChipGroup
                ariaLabel="Categoría de estudio"
                options={tiposEstudio.map((t) => ({ value: String(t.IdExamenTipo), label: t.tipo }))}
                value={categoria ? String(categoria.IdExamenTipo) : null}
                onChange={elegirCategoria}
              />
            </div>

            {categoria && (
              <div style={{ marginTop: 11 }}>
                <ExamenPicker
                  items={examenesCategoria}
                  seleccionados={form.IdExamenTipo}
                  onToggle={alternarExamen}
                  placeholder={`Buscar dentro de ${categoria.tipo}…`}
                  vacio="No hay exámenes en esta categoría para el seguro del paciente."
                />
                <div className="hcf__hint" style={{ marginTop: 6 }}>
                  Selección múltiple dentro de la misma categoría.
                  {form.IdExamenTipo.length > 0 && ` ${form.IdExamenTipo.length} seleccionado(s).`}
                </div>
              </div>
            )}
            {!categoria && intentado && faltantes.includes("Estudio") && (
              <div className="hcf__error" style={{ marginTop: 8 }} role="alert">
                Elige una categoría y al menos un examen.
              </div>
            )}
          </Section>

          <Section title="2 · Prioridad y logística">
            <div className="hcgrid hcgrid--3">
              <Field label="Prioridad" hint="Referencia para el servicio; no se guarda todavía.">
                <Segmented
                  ariaLabel="Prioridad"
                  options={PRIORIDADES}
                  value={prioridad}
                  onChange={setPrioridad}
                />
              </Field>

              <Field label="Sala" htmlFor="hc-img-sala">
                <input
                  id="hc-img-sala"
                  type="text"
                  placeholder="Sin asignar"
                  value={form.sala}
                  onChange={(e) => setForm({ ...form, sala: e.target.value })}
                />
              </Field>

              <Field label="Fecha de la orden" required htmlFor="hc-img-fecha">
                <input
                  id="hc-img-fecha"
                  type="date"
                  min={new Date().toISOString().split("T")[0]}
                  value={form.fecha}
                  onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                />
              </Field>
            </div>

            <Field label="Condiciones del paciente" hint="Se piden una sola vez. Referencia para el servicio; no se guardan todavía.">
              <CheckInline
                ariaLabel="Condiciones del paciente"
                items={condiciones}
                onToggle={(item, checked) =>
                  setCondiciones((cs) => cs.map((c) => (c.id === item.id ? { ...c, checked } : c)))
                }
              />
            </Field>
          </Section>

          <Section title="3 · Justificación clínica">
            <div className="hcgrid hcgrid--2">
              <Field
                label="Motivo de la solicitud"
                required
                htmlFor="hc-img-motivo"
                error={intentado && !form.motivo.trim() ? "Este campo es obligatorio" : null}
              >
                <textarea
                  id="hc-img-motivo"
                  ref={motivoRef}
                  rows={4}
                  placeholder="Requerido para enviar la orden"
                  aria-invalid={intentado && !form.motivo.trim() ? "true" : undefined}
                  value={form.motivo}
                  onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                />
              </Field>

              <Field
                label="Resumen clínico"
                htmlFor="hc-img-resumen"
                hint={
                  enfermedadActual ? (
                    <button
                      type="button"
                      className="hcbtn hcbtn--link"
                      style={{ minHeight: 0, padding: 0 }}
                      onClick={() => setForm({ ...form, resumen_clinico: enfermedadActual })}
                    >
                      Copiar de enfermedad actual
                    </button>
                  ) : null
                }
              >
                <textarea
                  id="hc-img-resumen"
                  rows={4}
                  placeholder="Opcional"
                  value={form.resumen_clinico}
                  onChange={(e) => setForm({ ...form, resumen_clinico: e.target.value })}
                />
              </Field>
            </div>
          </Section>
        </>
      ) : (
        <Section title="Diagnósticos de esta consulta" hint="Se registran en la consulta, no aquí.">
          {diagnosticos.length === 0 ? (
            <div className="hclist__vacio">
              Aún no hay diagnósticos en esta consulta. Se agregan en la sección Diagnósticos de la hoja.
            </div>
          ) : (
            <table className="cev__list" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Código CIE</th>
                  <th>Tipo</th>
                  <th>Observaciones</th>
                  <th aria-label="Acción" />
                </tr>
              </thead>
              <tbody>
                {diagnosticos.map((d) => (
                  <tr key={d.iddiagnostico_cita_paciente}>
                    <td>{d.diagnostico?.codigo}</td>
                    <td style={{ textTransform: "capitalize" }}>{(d.typo_diagnostico || "").toLowerCase()}</td>
                    <td>{d.comentario}</td>
                    <td>
                      <button
                        type="button"
                        className="hcbtn hcbtn--link"
                        onClick={() => borrarDiagnostico(d.iddiagnostico_cita_paciente)}
                      >
                        Quitar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      )}
    </HCModal>
  );
}
